import axiosWrapper from './axiosWrapper';
import { getScenes } from '../cache/dataClient';
import cacheSync from '../cache/cacheSync';
import { cacheUpdate } from '../cache/cacheHelpers';

/**
 * URL keys to try for DELETE /scenes/:id (Lexical id, then Mongo _id).
 * @param {string|{ id?: string, _id?: string }} sceneIdOrDoc
 * @returns {string[]}
 */
function sceneDeleteUrlKeys(sceneIdOrDoc) {
  if (sceneIdOrDoc != null && typeof sceneIdOrDoc === 'object' && !Array.isArray(sceneIdOrDoc)) {
    const keys = [];
    if (sceneIdOrDoc.id != null && sceneIdOrDoc.id !== '') {
      keys.push(String(sceneIdOrDoc.id));
    }
    if (sceneIdOrDoc._id != null && sceneIdOrDoc._id !== '') {
      const mongo = String(sceneIdOrDoc._id);
      if (!keys.includes(mongo)) keys.push(mongo);
    }
    return keys;
  }
  if (sceneIdOrDoc != null && sceneIdOrDoc !== '') return [String(sceneIdOrDoc)];
  return [];
}

/**
 * Service for managing scene data
 */
const sceneService = {
  /**
   * Add a scene to a chapter
   * @param {string} chapterId - The chapter ID
   * @param {Object} sceneData - The scene data to add
   * @returns {Promise<Object>} The created scene data
   */
  async addScene(chapterId, sceneData) {
    const response = await axiosWrapper({
      method: 'post',
      path: `chapters/${chapterId}/scenes`,
      data: sceneData,
    });
    // Update cache after successful creation
    if (!response.error) {
      cacheSync.syncAfterMutation('scenes', response, 'create').catch((err) => {
        console.warn('[sceneService] Error updating cache after create:', err);
      });
    }
    return response;
  },

  /**
   * Get all scenes for a chapter (offline-first)
   * @param {string} chapterId - The chapter ID
   * @param {Object} options - Options including useCache
   * @returns {Promise<Object>} The scenes data
   * @example
   * // Successful response structure:
   * {
   *   "data": {
   *     "msg": "Scenes retrieved successfully",
   *     "chapter": {
   *       "_id": "68fd7be102b6f9628a492b1f",
   *       "name": "Chapter 1",
   *       "chapterNumber": 1
   *     },
   *     "scenes": [
   *       {
   *         "_id": "68fd7f0e02b6f9628a492b38",
   *         "chapterId": "68fd7be102b6f9628a492b1f",
   *         "id": "scene_1761443598407_i72ndkpcf",
   *         "name": "a",
   *         "type": "paragraph",
   *         "position": 0,
   *         "data": { ... },
   *         "createdAt": "2025-10-26T01:53:18.408Z",
   *         "updatedAt": "2025-10-27T02:10:20.300Z"
   *       }
   *     ],
   *     "totalScenes": 2
   *   },
   *   "error": null
   * }
   */
  async getScenesByChapter(chapterId, options = {}) {
    const { useCache = true } = options;
    const result = await getScenes(chapterId, { useCache });
    if (result.error && !result.fromCache) {
      return { data: null, error: result.error };
    }

    // Return in format expected by existing code
    return {
      data: { scenes: result.data || [] },
      error: null,
      fromCache: result.fromCache,
    };
  },

  /**
   * Fetch scene data by ID
   * @param {string} sceneId - The scene ID
   * @returns {Promise<Object>} The scene data
   */
  async getSceneById(sceneId) {
    const response = await axiosWrapper({
      method: 'get',
      path: `scenes/${sceneId}`,
    });
    return response;
  },

  /**
   * Persist Lexical editor state (matches manuscript EditorBlock saves).
   * @param {string} sceneId - Scene id field used in URLs (Lexical scene id)
   * @param {Object} editorStateJson - Full Lexical serialized state { root, ... }
   * @param {Object} [options]
   * @param {Object} [options.sceneDocForCache] - Scene row shape for IndexedDB merge (needs _id)
   */
  async putEditorState(sceneId, editorStateJson, options = {}) {
    const { sceneDocForCache } = options;
    const response = await axiosWrapper({
      method: 'put',
      path: `scenes/${sceneId}/editor-state`,
      data: { editorState: editorStateJson },
    });
    if (!response.error && sceneDocForCache?._id) {
      try {
        await cacheUpdate('scenes', {
          ...sceneDocForCache,
          data: editorStateJson,
          wordCount:
            typeof response.data?.scene?.wordCount === 'number'
              ? response.data.scene.wordCount
              : sceneDocForCache.wordCount,
          updatedAt: new Date().toISOString(),
        });
      } catch (err) {
        console.warn('[sceneService] IndexedDB scene cache update failed:', err);
      }
    }
    return response;
  },

  /**
   * Update scene data
   * @param {string} sceneId - The scene ID
   * @param {Object} sceneData - The scene data to update
   * @returns {Promise<Object>} The updated scene data
   */
  async updateScene(sceneId, sceneData) {
    const response = await axiosWrapper({
      method: 'put',
      path: `scenes/${sceneId}`,
      data: sceneData,
    });
    // Update cache after successful update
    if (!response.error) {
      cacheSync.syncAfterMutation('scenes', response, 'update').catch((err) => {
        console.warn('[sceneService] Error updating cache after update:', err);
      });
    }
    return response;
  },

  /**
   * Delete a scene (soft delete).
   * @param {string|{ id?: string, _id?: string }} sceneIdOrDoc - Lexical id string, Mongo id string, or scene row
   * @returns {Promise<Object>} The deletion result; error null if server returns 404 (already gone)
   */
  async deleteScene(sceneIdOrDoc) {
    const keys = sceneDeleteUrlKeys(sceneIdOrDoc);
    if (keys.length === 0) {
      return { data: null, error: new Error('Scene id required') };
    }

    const tryKey = async (index) => {
      if (index >= keys.length) {
        cacheSync.syncAfterMutation('scenes', { id: keys[0] }, 'delete').catch((err) => {
          console.warn('[sceneService] Error updating cache after delete:', err);
        });
        return { data: null, error: null };
      }
      const sceneId = keys[index];
      const response = await axiosWrapper({
        method: 'delete',
        path: `scenes/${encodeURIComponent(sceneId)}`,
      });
      if (!response.error) {
        cacheSync.syncAfterMutation('scenes', { id: sceneId }, 'delete').catch((err) => {
          console.warn('[sceneService] Error updating cache after delete:', err);
        });
        return response;
      }
      if (response.error?.response?.status !== 404) {
        return response;
      }
      return tryKey(index + 1);
    };

    return tryKey(0);
  },
};

export default sceneService;
